/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type PaymentMode } from '@therapy/shared-validators';

import { apiClient } from './client.js';

import type { Page } from './resources.js';

export type { Page };

export type InvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'overdue' | 'void';
export type PaymentMethod =
  | 'cash'
  | 'card'
  | 'upi'
  | 'bank_transfer'
  | 'razorpay'
  | 'insurance'
  | 'cheque';
export type PaymentStatus = 'pending' | 'captured' | 'failed' | 'refunded';

/** Query params for the paginated invoice list (offset/limit + search + status). */
export interface InvoicesPageParams {
  readonly limit: number;
  readonly offset: number;
  readonly q?: string | undefined;
  readonly status?: InvoiceStatus | undefined;
}

export interface Invoice {
  readonly id: string;
  readonly clientId: string;
  readonly serviceItemId: string | null;
  readonly invoiceNumber: string;
  readonly status: InvoiceStatus;
  readonly amountPaise: number;
  readonly amountPaidPaise: number;
  readonly notes: string | null;
  readonly issuedAt: string | null;
  readonly dueDate: string | null;
  readonly createdAt: string;
  readonly lastPaymentMethod: string | null;
  readonly lastPaymentRef: string | null;
}

/** A service-catalogue / fee-schedule entry (P1-9). */
export interface ServiceItem {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly unitPricePaise: number;
  readonly active: boolean;
  readonly sortOrder: number;
}

export interface ServiceItemPayload {
  readonly name: string;
  readonly description?: string;
  readonly unitPricePaise: number;
  readonly active?: boolean;
}

export interface Payment {
  readonly id: string;
  readonly invoiceId: string;
  readonly clientId: string;
  readonly amountPaise: number;
  readonly method: PaymentMethod;
  readonly status: PaymentStatus;
  readonly providerRef: string | null;
  readonly paidAt: string;
}

export interface CreateInvoicePayload {
  readonly clientId: string;
  readonly amountPaise: number;
  readonly dueDate?: string;
  readonly notes?: string;
  readonly status?: 'draft' | 'sent';
  readonly serviceItemId?: string;
}

export interface RecordPaymentPayload {
  readonly amountPaise: number;
  readonly method: PaymentMode;
  readonly reference?: string;
  readonly paymentDate?: string;
}

/** A credit note issued against a paid (locked) invoice (CR-010). */
export interface CreditNote {
  readonly id: string;
  readonly invoiceId: string;
  readonly clientId: string;
  readonly creditNumber: string;
  readonly amountPaise: number;
  readonly reason: string;
  readonly createdAt: string;
}

export interface IssueCreditNotePayload {
  readonly amountPaise: number;
  readonly reason: string;
}

const BASE = '/finance/invoices';

/** One open invoice on the Finance Overview "Outstanding Invoices" list. */
export interface OutstandingInvoice {
  readonly id: string;
  readonly invoiceNumber: string;
  readonly clientName: string;
  readonly balancePaise: number;
  readonly dueDate: string | null;
  readonly status: InvoiceStatus;
}

/** Finance Overview read-model (Figma 4 cards + outstanding list). */
export interface FinanceOverview {
  readonly revenuePaise: number;
  readonly outstandingPaise: number;
  readonly processedTodayPaise: number;
  readonly netProfitPaise: number;
  readonly outstanding: readonly OutstandingInvoice[];
}

export const financeApi = {
  async list(): Promise<readonly Invoice[]> {
    const { data } = await apiClient.get<readonly Invoice[]>(BASE);
    return data;
  },
  async listPage(params: InvoicesPageParams): Promise<Page<Invoice>> {
    const { data } = await apiClient.get<Page<Invoice>>(`${BASE}/page`, { params });
    return data;
  },
  async create(payload: CreateInvoicePayload): Promise<Invoice> {
    const { data } = await apiClient.post<Invoice>(BASE, payload);
    return data;
  },
  async payments(id: string): Promise<readonly Payment[]> {
    const { data } = await apiClient.get<readonly Payment[]>(`${BASE}/${id}/payments`);
    return data;
  },
  async recordPayment(id: string, payload: RecordPaymentPayload): Promise<Invoice> {
    const { data } = await apiClient.post<Invoice>(`${BASE}/${id}/payments`, payload);
    return data;
  },
  /** Parent self-service: pay the invoice's outstanding balance (P0-3). */
  async payOwn(id: string): Promise<Invoice> {
    const { data } = await apiClient.post<Invoice>(`${BASE}/${id}/pay`);
    return data;
  },
  async overview(): Promise<FinanceOverview> {
    const { data } = await apiClient.get<FinanceOverview>('/finance/overview');
    return data;
  },
  async creditNotes(id: string): Promise<readonly CreditNote[]> {
    const { data } = await apiClient.get<readonly CreditNote[]>(`${BASE}/${id}/credit-notes`);
    return data;
  },
  async issueCreditNote(id: string, payload: IssueCreditNotePayload): Promise<CreditNote> {
    const { data } = await apiClient.post<CreditNote>(`${BASE}/${id}/credit-notes`, payload);
    return data;
  },
  async remind(id: string): Promise<{ sent: boolean; email: string | null }> {
    const { data } = await apiClient.post<{ sent: boolean; email: string | null }>(
      `${BASE}/${id}/remind`,
    );
    return data;
  },
  /** Download the payment-receipt PDF for an invoice (CR-010). */
  async downloadReceipt(invoice: Invoice): Promise<void> {
    const { data } = await apiClient.get<Blob>(`${BASE}/${invoice.id}/receipt`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${invoice.invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};

const CATALOGUE_BASE = '/finance/service-items';

export const catalogueApi = {
  async list(activeOnly = false): Promise<readonly ServiceItem[]> {
    const { data } = await apiClient.get<readonly ServiceItem[]>(CATALOGUE_BASE, {
      params: activeOnly ? { activeOnly: 'true' } : {},
    });
    return data;
  },
  async create(payload: ServiceItemPayload): Promise<ServiceItem> {
    const { data } = await apiClient.post<ServiceItem>(CATALOGUE_BASE, payload);
    return data;
  },
  async update(id: string, payload: Partial<ServiceItemPayload>): Promise<ServiceItem> {
    const { data } = await apiClient.patch<ServiceItem>(`${CATALOGUE_BASE}/${id}`, payload);
    return data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`${CATALOGUE_BASE}/${id}`);
  },
};
